import { request } from '@/utils/http'
import type { PageRequest } from '@/types/api'
import type { TransportConsignmentWaybillVO } from './transport-consignment'

export type IdValue = string | number

export interface DistributionShipmentPageRequest extends PageRequest {
  [key: string]: unknown
  fhNo?: string
  orderStatus?: number
  splitStatus?: number
  source?: number
  orderType?: number
  docSubtype?: number
  goodsType?: string
  bizTags?: string
  cusShortName?: string
  cusName?: string
  cusNo?: string
  regionLevelOne?: string
  regionName?: string
  regionLevelTwo?: string
  regionLevel2Name?: string
  regionLevelThree?: string
  regionLevel3Name?: string
  costCenter?: string
  costCenterName?: string
  bizDept?: string
  departmentName?: string
  salesType?: string
  salesTypeName?: string
  salesPlatform?: number
  originWhCode?: string
  originWh?: string
  destinationWhCode?: string
  destinationWh?: string
  expectShipDateStart?: string
  expectShipDateEnd?: string
  actShipDateStart?: string
  actShipDateEnd?: string
  expectArrivalDateStart?: string
  expectArrivalDateEnd?: string
  actArrivalDateStart?: string
  actArrivalDateEnd?: string
  originCreateTimeStart?: string
  originCreateTimeEnd?: string
  originCreator?: string
  orderRemark?: string
  orderReferenceRemark?: string
  logisticsOperator?: string | number
  logisticsOperatorText?: string
  insure?: boolean
  createdAtStart?: string
  createdAtEnd?: string
  updatedAtStart?: string
  updatedAtEnd?: string
  crossBorder?: boolean
  logisticsDirection?: number
  expectTransportMode?: number
  mainTransportMode?: number
  splitSegment?: boolean
  splitStrategy?: number
  customerConsignment?: boolean
  customsFlag?: boolean
  tradeCountry?: string
  tradeCountryEn?: string
  destCountry?: string
  destCountryEn?: string
  tradeChain?: string
  transportTerms?: string
  customsTradeTerm?: string
  clearanceTradeTerm?: string
  expressSupplier?: string
  expressNo?: string
  customsType?: number
  currency?: string
  totalAmount?: number
  totalTax?: number
  clearanceTotalAmountCurrency?: string
  clearanceTotalAmount?: number
  exchangeRate?: number
  hsCode?: string
  originCountry?: string
  bizRemark?: string
}

export interface TransportPlanDTO {
  planId?: IdValue
  planNo?: string
  relatePlanNo?: string
  segment?: string | number
  transportSegment?: number
  mainTransportMode?: number
  segmentOriginId?: IdValue
  originPoint?: string
  segmentOrigin?: string
  segmentDestinationId?: IdValue
  segmentDestination?: string
  originEndpointType?: 'COUNTRY' | 'PORT'
  destEndpointType?: 'COUNTRY' | 'PORT'
  mainlineOriginCountry?: string
  mainlineDestCountry?: string
  portOfLoadingId?: string
  portOfLoadingCode?: string
  portOfLoadingName?: string
  portOfDischargeId?: string
  portOfDischargeCode?: string
  portOfDischargeName?: string
  destPoint?: string
  planStatus?: string | number
  planOrderStatus?: number
  trackingStatus?: string | number
  traceStatus?: number
  currentTrackingNode?: string
}

export interface DistributionShipmentTransportPlanUpdateRequest {
  fhId: IdValue
  planId: IdValue
  transportSegment: number
  mainTransportMode: number
  segmentOrigin?: string
  segmentDestination?: string
}

export interface DistributionShipmentListVO {
  id: IdValue
  fhNo: string
  orderStatus?: number
  splitStatus?: number
  source?: number
  orderType?: number
  docSubtype?: number
  goodsType?: unknown
  bizTags?: unknown
  fhCateg?: string
  fhCategName?: string
  cusShortName?: string
  cusName?: string
  cusNo?: string
  regionLevelOne?: string
  regionName?: string
  regionLevelTwo?: string
  regionLevel2Name?: string
  regionLevelThree?: string
  regionLevel3Name?: string
  costCenter?: string
  costCenterName?: string
  bizDept?: string
  departmentName?: string
  salesType?: string
  salesTypeName?: string
  salesPlatform?: number
  originWhCode?: string
  originWh?: string
  destinationWhCode?: string
  destinationWh?: string
  expectShipDate?: string
  goodsReadyTime?: string
  outboundNoticeSuccessCount?: number
  actShipDate?: string
  expectArrivalDate?: string
  actArrivalDate?: string
  originCreateTime?: string
  originCreator?: string
  orderRemark?: string
  orderReferenceRemark?: string
  logisticsOperator?: string | number
  logisticsOperatorText?: string
  createdAt?: string
  updatedAt?: string
  insure?: boolean
  crossBorder?: boolean
  logisticsDirection?: number
  expectTransportMode?: number
  mainTransportMode?: number
  splitSegment?: boolean
  splitStrategy?: number
  customerConsignment?: boolean
  tradeCountry?: string
  tradeCountryEn?: string
  destCountry?: string
  destCountryEn?: string
  tradeChain?: string
  customsTradeTerm?: string
  clearanceTradeTerm?: string
  transportTerms?: string
  expressSupplier?: string
  expressNo?: string
  customsFlag?: boolean
  customsType?: number
  currency?: string
  totalAmount?: number
  totalTax?: number
  clearanceTotalAmountCurrency?: string
  clearanceTotalAmount?: number
  exchangeRate?: number
  hsCode?: string
  originCountry?: string
  bizRemark?: string
  currentTrackingNode?: string
  transportPlans?: TransportPlanDTO[]
  dateCreated?: string
  dateModified?: string
}

export interface DistributionShipmentPageResponse {
  records?: DistributionShipmentListVO[]
  list?: DistributionShipmentListVO[]
  total: number
  size?: number
  current?: number
  pages?: number
}

export interface DistributionShipmentSaveRequest {
  shipment: ShipmentSaveDTO
  logisticsInfo?: LogisticsInfoSaveDTO
  contactAddresses?: ContactAddressSaveDTO[]
  packageWd?: PackageWdSaveDTO
  products?: ProductInfoSaveDTO[]
}

export interface DistributionShipmentOutboundNoticeBatchRequest {
  ids: IdValue[]
}

export interface DistributionShipmentCancelRequest {
  id: IdValue
  cancelReason: string
}

export const DISTRIBUTION_SHIPMENT_CANCEL_RESULT = {
  CANCELLED: 'CANCELLED',
  WMS_ORDER_NOT_FOUND: 'WMS_ORDER_NOT_FOUND',
  WMS_CANCEL_FAILED: 'WMS_CANCEL_FAILED'
} as const

export type DistributionShipmentCancelResult =
  typeof DISTRIBUTION_SHIPMENT_CANCEL_RESULT[keyof typeof DISTRIBUTION_SHIPMENT_CANCEL_RESULT]

export interface DistributionShipmentCancelResultVO {
  result: DistributionShipmentCancelResult
  errorMessage?: string
}

export interface DistributionShipmentOutboundNoticeInvalidItemVO {
  id?: IdValue
  fhNo?: string
  reasonCode?: string
  reasonMessage?: string
}

export interface DistributionShipmentOutboundNoticePrecheckVO {
  eligible: boolean
  invalidItems: DistributionShipmentOutboundNoticeInvalidItemVO[]
}

export interface DistributionShipmentOutboundNoticeResultItemVO {
  id?: IdValue
  fhNo?: string
  success: boolean
  failureReason?: string
}

export interface DistributionShipmentOutboundNoticeBatchResultVO {
  batchNo: string
  totalCount: number
  successCount: number
  failureCount: number
  items: DistributionShipmentOutboundNoticeResultItemVO[]
}

export interface TheoreticalChargeWeightProductDTO {
  skuCode?: string
  pcsQty?: number
  boxGauge?: string | null
  stdBoxQty?: number
}

export interface DistributionShipmentTheoreticalChargeWeightRequest {
  mainTransportMode?: number
  expectTransportMode?: number
  totalQtyExpect?: number
  totalBoxQtyExpect?: number
  totalVolumeExpect?: number
  totalWeightExpect?: number
  lengthExpect?: number
  widthExpect?: number
  heightExpect?: number
  products?: TheoreticalChargeWeightProductDTO[]
}

export interface SkippedTheoreticalChargeWeightProductVO {
  skuCode?: string
  reasonCode?: string
  part?: 'FULL_BOX' | 'LOOSE_ITEM'
  missingFields?: string[]
}

export interface DistributionShipmentTheoreticalChargeWeightVO {
  calculable?: boolean
  reasonCode?: string
  transportMode?: number
  divisor?: number
  totalQtyExpect?: number
  totalBoxQtyExpect?: number
  totalVolumeExpect?: number
  totalWeightExpect?: number
  lengthExpect?: number
  widthExpect?: number
  heightExpect?: number
  volumeWeight?: number
  totalWeightCountExpect?: number
  calculatedFromProductMaterials?: boolean
  skippedProducts?: SkippedTheoreticalChargeWeightProductVO[]
}

export const THEORETICAL_PACKAGE_CALCULATION_REASON = {
  MDM_QUERY_FAILED: 'MDM_QUERY_FAILED'
} as const

export interface DistributionShipmentPlanRequest {
  id: IdValue
  mainTransportMode: number
  splitSegment: boolean
  splitStrategy?: number
  originEndpointType?: 'COUNTRY' | 'PORT'
  destEndpointType?: 'COUNTRY' | 'PORT'
  mainlineOriginCountry?: string
  mainlineDestCountry?: string
  portOfLoadingId?: string
  portOfLoadingCode?: string
  portOfLoadingName?: string
  portOfDischargeId?: string
  portOfDischargeCode?: string
  portOfDischargeName?: string
  originName?: string
  destinationName?: string
}

export interface ContactAddressImportRow {
  rowNumber?: number
  partyType?: number
  partyTypeText?: string
  company?: string
  contactName?: string
  phone?: string
  country?: string
  province?: string
  city?: string
  district?: string
  address?: string
}

export interface ContactAddressImportError {
  rowNumber?: number
  errorCode?: string
  params?: Record<string, unknown>
  message?: string
}

export interface ContactAddressImportResult {
  rows?: ContactAddressImportRow[]
  errors?: ContactAddressImportError[]
  successCount?: number
  errorCount?: number
}

export interface DistributionShipmentDetailVO {
  shipment?: ShipmentDetailDTO
  logisticsInfo?: LogisticsInfoSaveDTO
  contactAddresses?: ContactAddressSaveDTO[]
  packageWd?: PackageWdSaveDTO
  products?: ProductInfoSaveDTO[]
  transportPlans?: TransportPlanDTO[]
}

export interface ShipmentSaveDTO {
  id?: IdValue
  fhNo?: string
  source?: number
  orderType?: number
  docSubtype?: number
  goodsType?: unknown
  bizTags?: unknown
  fhCateg?: string
  fhCategName?: string
  cusShortName?: string
  cusName?: string
  cusNo?: string
  regionLevelOne?: string
  regionName?: string
  regionLevelTwo?: string
  regionLevel2Name?: string
  regionLevelThree?: string
  regionLevel3Name?: string
  costCenter?: string
  costCenterName?: string
  bizDept?: string
  departmentName?: string
  salesType?: string
  salesTypeName?: string
  salesPlatform?: number
  originWhCode?: string
  originWh?: string
  destinationWhCode?: string
  destinationWh?: string
  expectShipDate?: string
  goodsReadyTime?: string
  expectArrivalDate?: string
  originCreator?: string
  orderRemark?: string
  orderReferenceRemark?: string
  logisticsOperator?: string | number
  logisticsOperatorText?: string
  insure?: boolean
}

export interface ShipmentDetailDTO extends ShipmentSaveDTO {
  orderStatus?: number
  splitStatus?: number
  cancelReason?: string
  actShipDate?: string
  actArrivalDate?: string
  originCreateTime?: string
  createdAt?: string
  updatedAt?: string
  dateCreated?: string
  dateModified?: string
}

export interface LogisticsInfoSaveDTO {
  crossBorder?: boolean
  palletized?: boolean
  logisticsDirection?: number
  expectTransportMode?: number
  mainTransportMode?: number
  splitSegment?: boolean
  splitStrategy?: number
  customerConsignment?: boolean
  tradeCountry?: string
  tradeCountryEn?: string
  destCountry?: string
  destCountryEn?: string
  tradeChain?: string
  customsTradeTerm?: string
  clearanceTradeTerm?: string
  transportTerms?: string
  expressSupplier?: string
  expressNo?: string
  customsFlag?: boolean
  customsType?: number
  currency?: string
  totalAmount?: number
  totalTax?: number
  clearanceTotalAmountCurrency?: string
  clearanceTotalAmount?: number
  exchangeRate?: number
  hsCode?: string
  originCountry?: string
  bizRemark?: string
}

export type AddressLocationValue = number | string

export interface ContactAddressSaveDTO {
  id?: IdValue
  partyType?: number
  company?: string
  companyNameCn?: string
  companyNameEn?: string
  companyCountryCode?: string
  contactName?: string
  phone?: string
  country?: AddressLocationValue
  province?: AddressLocationValue
  city?: AddressLocationValue
  /** 1 基础资料，2 手动输入；空值兼容历史数据。 */
  citySource?: 1 | 2 | null
  district?: AddressLocationValue
  town?: string
  address?: string
  postcode?: string
  externalId?: string
  externalSource?: string
}

export interface PackageWdSaveDTO {
  volumeUnit?: number
  weightUnit?: number
  lengthUnit?: number
  weightCount?: number
  totalQtyExpect?: number
  totalBoxQtyExpect?: number
  totalPalletQtyExpect?: number
  totalVolumeExpect?: number
  totalWeightExpect?: number
  totalWeightCountExpect?: number
  lengthExpect?: number
  widthExpect?: number
  heightExpect?: number
  totalQtyWh?: number
  totalBoxQtyWh?: number
  totalPalletQtyWh?: number
  totalVolumeWh?: number
  totalWeightWh?: number
  totalWeightCountWh?: number
  lengthWh?: number
  widthWh?: number
  heightWh?: number
  totalQtySupplier?: number
  totalVolumeSupplier?: number
  totalWeightSupplier?: number
  totalWeightCountSupplier?: number
  lengthSupplier?: number
  widthSupplier?: number
  heightSupplier?: number
  externalId?: string
  externalSource?: string
}

export interface ProductInfoSaveDTO {
  id?: string | number
  skuCode?: string
  productName?: string
  salesOrderNo?: string
  docType?: string
  productModel?: string
  brand?: string
  specialAttributes?: string | null
  barCode?: string | null
  pcsQty?: number
  boxGauge?: string | null
  stdBoxQty?: number
  actualBoxQty?: number
  packingMethod?: string
  unitPrice?: number
  amtNet?: number
  hsCode?: string
  cisOsNo?: string
  cnDesc?: string
  enDesc?: string
  customsDecl?: string
  amountOrigin?: number
  unitPriceOrigin?: number
  unitPriceNet?: number
  amtFh?: number
  tax?: number
  taxRatio?: number
  unitPriceAg?: number
  amountNetAg?: number
  amountTaxAg?: number
  amountAllAg?: number
  currencyAg?: string
  unitPriceBg?: number
  amountNetBg?: number
  amountTaxBg?: number
  amountAllBg?: number
  currencyBg?: string
  externalId?: string
  externalSource?: string
}

export interface NsEstimateBillManualPushStatusVO {
  visible: boolean
  enabled: boolean
  message?: string
}

export interface NsEstimateBillManualPushFailedGroupVO {
  subsidiary?: string
  currency?: string
  supplierId?: string
  supplierName?: string
  message?: string
  missingFields?: string[]
}

export interface NsEstimateBillManualPushResultVO {
  fhNo?: string
  success: boolean
  message?: string
  successGroupCount: number
  failedGroupCount: number
  failedGroups?: NsEstimateBillManualPushFailedGroupVO[]
}

const baseUrl = '/booking/distribution-shipment'

export const distributionShipmentApi = {
  page: (params: DistributionShipmentPageRequest) =>
    request.post<DistributionShipmentPageResponse>(`${baseUrl}/page`, params),
  detail: (id: IdValue) =>
    request.post<DistributionShipmentDetailVO>(`${baseUrl}/detail`, { id }),
  precheckOutboundNotice: (params: DistributionShipmentOutboundNoticeBatchRequest) =>
    request.post<DistributionShipmentOutboundNoticePrecheckVO>(
      `${baseUrl}/outbound-notice/precheck`,
      params,
      { showErrorMessage: false } as any
    ),
  pushOutboundNotice: (params: DistributionShipmentOutboundNoticeBatchRequest) =>
    request.post<DistributionShipmentOutboundNoticeBatchResultVO>(
      `${baseUrl}/outbound-notice/push`,
      params,
      { showErrorMessage: false, timeout: 120000 } as any
    ),
  queryNsEstimateBillManualPushStatus: (id: IdValue) =>
    request.post<NsEstimateBillManualPushStatusVO>(
      `${baseUrl}/ns-estimate-bill/manual-push/status`,
      { id },
      { showErrorMessage: false }
    ),
  manualPushNsEstimateBill: (id: IdValue) =>
    request.post<NsEstimateBillManualPushResultVO>(
      `${baseUrl}/ns-estimate-bill/manual-push`,
      { id },
      { showErrorMessage: false, timeout: 120000 }
    ),
  save: (params: DistributionShipmentSaveRequest) =>
    request.post<IdValue>(`${baseUrl}/save`, params),
  /** 根据商品包装主数据预计算理论件重尺和理论计费重。 */
  calculateTheoreticalChargeWeight: (
    params: DistributionShipmentTheoreticalChargeWeightRequest
  ) =>
    request.post<DistributionShipmentTheoreticalChargeWeightVO>(
      `${baseUrl}/package-wd/theoretical-charge-weight/calculate`,
      params,
      { showErrorMessage: false }
    ),
  plan: (params: DistributionShipmentPlanRequest) =>
    request.post<boolean>(`${baseUrl}/plan`, params),
  cancel: (params: DistributionShipmentCancelRequest) =>
    request.post<DistributionShipmentCancelResultVO>(
      `${baseUrl}/cancel`,
      params,
      { showErrorMessage: false, timeout: 150000 }
    ),
  cancelTmsOnly: (params: DistributionShipmentCancelRequest) =>
    request.post<boolean>(`${baseUrl}/cancel/tms-only`, params, { showErrorMessage: false }),
  cancelPlan: (id: IdValue) =>
    request.post<boolean>(`${baseUrl}/cancel-plan`, { id }),
  freeze: (id: IdValue) =>
    request.post<boolean>(`${baseUrl}/freeze`, { id }),
  unfreeze: (id: IdValue) =>
    request.post<boolean>(`${baseUrl}/unfreeze`, { id }),
  updateTransportPlan: (params: DistributionShipmentTransportPlanUpdateRequest) =>
    request.post<boolean>(`${baseUrl}/transport-plan/update`, params),
  listTrackingWaybills: (id: IdValue) =>
    request.post<TransportConsignmentWaybillVO[]>(`${baseUrl}/tracking/waybill/list`, { id }),
  importContactAddress: (file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return request.post<ContactAddressImportResult>(`${baseUrl}/contact-address/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }
}

export default distributionShipmentApi
